/** In-memory + sessionStorage cache for GET /procureiq/week-orders. */

const TTL_MS = 10 * 60 * 1000;
const STORAGE_PREFIX = "procureiq_week_orders_v1:";
const memory = new Map();

function cacheKey(locationId, weekStart) {
  return `${Number(locationId)}|${weekStart || ""}`;
}

function storageKey(locationId, weekStart) {
  return `${STORAGE_PREFIX}${cacheKey(locationId, weekStart)}`;
}

function isFresh(at) {
  return typeof at === "number" && Date.now() - at < TTL_MS;
}

/**
 * @param {number|string} locationId
 * @param {string} [weekStart] Monday YYYY-MM-DD or "" for API default
 * @returns {object|null}
 */
export function getWeekOrdersCache(locationId, weekStart = "") {
  if (locationId == null || locationId === "") return null;
  const k = cacheKey(locationId, weekStart);
  const mem = memory.get(k);
  if (mem && isFresh(mem.at) && mem.data) return mem.data;

  try {
    const raw = sessionStorage.getItem(storageKey(locationId, weekStart));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !isFresh(parsed.at) || !parsed.data) {
      sessionStorage.removeItem(storageKey(locationId, weekStart));
      return null;
    }
    memory.set(k, parsed);
    return parsed.data;
  } catch {
    return null;
  }
}

/**
 * @param {number|string} locationId
 * @param {string} [weekStart]
 * @param {object} data
 */
export function setWeekOrdersCache(locationId, weekStart = "", data) {
  if (locationId == null || locationId === "" || !data) return;
  const entry = { at: Date.now(), data };
  const k = cacheKey(locationId, weekStart);
  memory.set(k, entry);
  try {
    sessionStorage.setItem(storageKey(locationId, weekStart), JSON.stringify(entry));
  } catch {
    /* quota / private mode — memory still helps within the SPA session */
  }
}

/**
 * @param {number|string} [locationId] omit to clear all ProcureIQ week caches
 * @param {string} [weekStart]
 */
export function invalidateWeekOrdersCache(locationId, weekStart = "") {
  if (locationId == null || locationId === "") {
    memory.clear();
    try {
      const keys = [];
      for (let i = 0; i < sessionStorage.length; i += 1) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith(STORAGE_PREFIX)) keys.push(key);
      }
      keys.forEach((key) => sessionStorage.removeItem(key));
    } catch {
      /* ignore */
    }
    return;
  }
  const k = cacheKey(locationId, weekStart);
  memory.delete(k);
  try {
    sessionStorage.removeItem(storageKey(locationId, weekStart));
  } catch {
    /* ignore */
  }
}
